import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AdapterStatus {
  provider: string;
  displayName: string;
  totalSystems: number;
  onlineSystems: number;
  staleSystems: number;
  offlineSystems: number;
  authFailedSystems: number;
  lastSuccessfulPoll: Date | null;
  avgMinutesSincePoll: number | null;
}

interface AdapterRow {
  out_provider: string;
  out_total_systems: number;
  out_online_systems: number;
  out_stale_systems: number;
  out_offline_systems: number;
  out_auth_failed_systems: number;
  out_last_successful_poll: string | null;
  out_avg_minutes_since_poll: number | null;
}

const DISPLAY_NAMES: Record<string, string> = {
  solark: 'Sol-Ark',
  eg4: 'EG4',
  franklinwh: 'FranklinWH',
  solaredge: 'SolarEdge',
  enphase: 'Enphase',
  tesla: 'Tesla',
  victron: 'Victron',
};

async function fetchAdapterHealth(): Promise<AdapterStatus[]> {
  const { data, error } = await supabase.rpc('admin_adapter_health');
  if (error) throw error;

  return ((data as AdapterRow[]) ?? []).map((row) => ({
    provider: row.out_provider,
    displayName: DISPLAY_NAMES[row.out_provider] ?? row.out_provider,
    totalSystems: row.out_total_systems,
    onlineSystems: row.out_online_systems,
    staleSystems: row.out_stale_systems,
    offlineSystems: row.out_offline_systems,
    authFailedSystems: row.out_auth_failed_systems,
    lastSuccessfulPoll: row.out_last_successful_poll ? new Date(row.out_last_successful_poll) : null,
    avgMinutesSincePoll: row.out_avg_minutes_since_poll,
  }));
}

export function useAdminAdapterHealth(enabled: boolean, refetchInterval: number | false) {
  return useQuery({
    queryKey: ['admin-adapter-health'],
    queryFn: fetchAdapterHealth,
    enabled,
    refetchInterval,
  });
}
